import { useMemo, useState } from "react";
import { CalendarDays, Flame, HeartPulse, Bell } from "lucide-react";
import { useData } from "@/context/DataContext";
import { useStats } from "@/hooks/useStats";
import { money, today } from "@/lib/money";

const addDays = (date, n) => {
  const d = new Date(`${date}T12:00:00`);
  d.setDate(d.getDate() + n);
  return [d.getFullYear(), String(d.getMonth() + 1).padStart(2, "0"), String(d.getDate()).padStart(2, "0")].join("-");
};

export function calculateStreak(rows, current = today()) {
  const days = new Set(rows.filter((r) => Number(r.amt) > 0).map((r) => r.date));
  let cursor = days.has(current) ? current : addDays(current, -1);
  let streak = 0;
  while (days.has(cursor) && streak < 10000) { streak++; cursor = addDays(cursor, -1); }
  return streak;
}

export default function FinancialPulse() {
  const { db } = useData();
  const st = useStats();
  const [heatmapDays, setHeatmapDays] = useState(35);
  const data = useMemo(() => {
    const current = today();
    const cells = Array.from({ length: heatmapDays }, (_, i) => addDays(current, i - heatmapDays + 1));
    const spent = {};
    db.expenses.forEach((r) => { spent[r.date] = (spent[r.date] || 0) + Number(r.amt); });
    const budget = db.settings.prefs.budgetMode === "overall"
      ? Number(db.settings.prefs.overallBudget || 0)
      : Object.values(db.settings.budgets).reduce((sum, v) => sum + Number(v), 0);
    const used = db.settings.prefs.budgetMode === "overall"
      ? st.budgetTotal
      : Object.keys(db.settings.budgets).reduce((sum, k) => sum + Number(st.budgetSpend[k] || 0), 0);
    const ratio = budget > 0 ? used / budget : null;
    // A simple spending-to-budget indicator, not a financial/credit score.
    const score = ratio === null ? null : Math.max(0, Math.round(100 - Math.max(0, ratio - 0.7) * 100));
    const alerts = [];
    if (ratio !== null && ratio >= 1) alerts.push(`Budget exceeded by ${money(used - budget)}.`);
    else if (ratio !== null && ratio >= .8) alerts.push(`Budget is ${Math.round(ratio * 100)}% used.`);
    db.recurring.filter((r) => !r.paused && r.next <= current).slice(0, 4)
      .forEach((r) => alerts.push(`${r.name}: recurring ${r.kind || "expense"} is due (${r.next}).`));
    db.loans.filter((l) => !l.ret && l.return_date && l.return_date <= current).slice(0, 4)
      .forEach((l) => alerts.push(`${l.what} (${l.who}): due ${l.return_date}.`));
    const goal = Number(db.settings.prefs.goal || 0);
    const saved = db.savings.reduce((sum, x) => sum + Number(x.amt), 0);
    if (goal > 0 && saved >= goal) alerts.push(`Your savings target of ${money(goal)} has been reached!`);
    return { cells, spent, budget, used, score, alerts, streak: calculateStreak(db.savings, current) };
  }, [db, st.budgetTotal, st.budgetSpend, heatmapDays]);
  return <section className="mm-card mm-rise space-y-4" aria-label="Financial pulse">
    <h2 className="mm-h2 flex items-center gap-2 text-lg"><HeartPulse size={19} className="text-[var(--ac)]" /> Financial pulse</h2>
    <div className="grid grid-cols-2 gap-3">
      <div className="rounded-xl bg-[var(--bg)] p-3"><div className="flex items-center gap-2 text-xs text-[var(--mu)]"><HeartPulse size={14} /> Budget health</div><p className="num mt-1 text-2xl font-bold">{data.score === null ? "—" : `${data.score}/100`}</p><p className="mt-1 text-xs text-[var(--mu)]">{data.score === null ? "Set a budget to see the indicator" : "Based only on budget use in the selected budget period"}</p></div>
      <div className="rounded-xl bg-[var(--bg)] p-3"><div className="flex items-center gap-2 text-xs text-[var(--mu)]"><Flame size={14} /> Savings streak</div><p className="num mt-1 text-2xl font-bold">{data.streak} day{data.streak === 1 ? "" : "s"}</p><p className="mt-1 text-xs text-[var(--mu)]">Days with a positive savings deposit</p></div>
    </div>
    <div><div className="mb-2 flex flex-wrap items-center gap-2 text-sm font-semibold"><CalendarDays size={16} className="text-[var(--ac)]" /> Spending calendar <label className="ml-auto text-xs font-normal text-[var(--mu)]">Show <select className="rounded-md border border-[var(--bd)] bg-[var(--bg)] p-1 text-[var(--tx)]" value={heatmapDays} onChange={(e) => setHeatmapDays(Number(e.target.value))} aria-label="Spending heatmap history length"><option value={35}>35 days</option><option value={90}>90 days</option><option value={365}>365 days</option></select></label></div>
      <div className="grid grid-cols-7 gap-1" aria-label="Spending calendar heatmap">{data.cells.map((date) => {
        const spent = data.spent[date] || 0;
        const step = spent <= 0 ? 0 : spent < 20 ? 1 : spent < 100 ? 2 : spent < 300 ? 3 : 4;
        return <div key={date} title={`${date}: ${money(spent)}`} aria-label={`${date}, ${money(spent)} spent`} className={`bp-heat bp-heat-${step}`} />;
      })}</div>
      <div className="mt-1 flex justify-between text-[10px] text-[var(--mu)]"><span>{heatmapDays} days including today</span><span>Today</span></div>
    </div>
    {db.settings.prefs.remindersEnabled !== false && <div><div className="mb-2 flex items-center gap-2 text-sm font-semibold"><Bell size={16} className="text-[var(--ac)]" /> In-app reminders</div>
      {data.alerts.length ? <ul className="space-y-1 text-xs text-[var(--mu)]">{data.alerts.map((a, i) => <li key={i} className="rounded-lg border border-[var(--bd)] px-3 py-2">{a}</li>)}</ul> : <p className="text-xs text-[var(--mu)]">No budget, debt or recurring reminders currently due.</p>}
    </div>}
  </section>;
}
